import { Badge, Box, Card, Container, Group, Image, Loader, SimpleGrid, Text, Title } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CommunityGem } from '../models/CommunityGem'
import { GemTag } from '../models/GemTag'
import { getAllTags, getPublishedGems } from '../data/communityGems.service'

function CommunityPage() {
  const navigate = useNavigate()
  const [gems, setGems] = useState<CommunityGem[]>([])
  const [tags, setTags] = useState<GemTag[]>([])
  const [activeTagIds, setActiveTagIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    Promise.all([getPublishedGems(), getAllTags()])
      .then(([gemsData, tagsData]) => {
        setGems(gemsData)
        setTags(tagsData)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  const toggleTag = (id: number) => {
    setActiveTagIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id])
  }

  const filteredGems = activeTagIds.length > 0
    ? gems.filter(g => g.tags.some(t => activeTagIds.includes(t.id)))
    : gems

  if (loading) return (
    <Box style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
      <Loader />
    </Box>
  )

  if (error) return (
    <Container py='xl'>
      <Text ta='center' c='red'>Failed to load Gemz. Please try again later.</Text>
    </Container>
  )

  return (
    <Container size='lg' py='xl'>
      <Title ta='center' mb='xs'>Hidden Gemz in Your Community</Title>
      <Text ta='center' c='dimmed' mb='xl'>Curated local businesses and cool places worth discovering</Text>

      <Group justify='center' gap='xs' mb='xl'>
        {[...tags].sort((a, b) => a.sortOrder - b.sortOrder).map(tag => (
          <Badge
            key={tag.id}
            size='lg'
            variant={activeTagIds.includes(tag.id) ? 'filled' : 'outline'}
            color='green'
            style={{ cursor: 'pointer' }}
            onClick={() => toggleTag(tag.id)}
          >
            {tag.name}
          </Badge>
        ))}
      </Group>

      {filteredGems.length === 0 ? (
        <Text ta='center' c='dimmed'>No Gemz found for the selected filters.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing='md'>
          {filteredGems.map(gem => (
            <Card
              key={gem.id}
              shadow='sm'
              radius='md'
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/community/${gem.id}`)}
            >
              {gem.photos[0] && (
                <Card.Section>
                  <Image src={gem.photos[0].imageUrl} height={160} alt={gem.name} />
                </Card.Section>
              )}
              <Box pt='sm'>
                <Text fw={700}>{gem.name}</Text>
                <Text size='sm' c='dimmed'>
                  {[gem.city, gem.state].filter(Boolean).join(', ') || '—'}
                </Text>
                <Group gap='xs' mt='xs'>
                  {gem.tags.map(t => (
                    <Badge key={t.id} size='sm' color='green' variant='light'>{t.name}</Badge>
                  ))}
                </Group>
              </Box>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Container>
  )
}

export default CommunityPage
